import type { DurableLeaseToken } from "./durableUploadSchema";
import type { DurableOwnershipPort } from "./ports";

/**
 * The one scheduler-lease wrapper every idle drain runs under.
 *
 * Three lanes (admin small file, async recording chunk stream, the async legacy recording drain)
 * carried the same publish/acquire/drain/republish/release sequence. The legacy copy released the
 * lease against a stale `remaining` snapshot rather than a live idle read, so work enqueued after
 * the release-time count but before `releaseScheduler` dropped the lease with the origin still
 * pending. `isIdle` here is always a fresh `countPending()` read, which is what the durable port's
 * release CAS is written to consume.
 */
export async function withSchedulerLease<T>(options: {
	ownership: DurableOwnershipPort;
	originKey: string;
	ownerId: string;
	ttlMs: number;
	/** Live count of drainable work for this origin. Called at entry, at release, and for `isIdle`. */
	countPending: () => Promise<number>;
	run: () => Promise<T>;
	/** Nothing to do. Defaults to `undefined` without running. */
	onIdle?: () => Promise<T | undefined> | T | undefined;
	/** Another owner holds the lease. Defaults to `undefined` without running. */
	onLeaseHeldElsewhere?: () => Promise<T | undefined> | T | undefined;
	/**
	 * Publish the idle observation before returning through `onIdle`. Admin's small-file lane
	 * deliberately does not touch the durable work row when it has nothing to schedule.
	 */
	noteIdleWork?: boolean;
	/**
	 * LSP-23117: replaces the plain `acquireScheduler` call, e.g. to stamp a liveness lock and take over a
	 * dead owner. It must acquire for this `originKey` and `ownerId`; the release below uses its token.
	 */
	acquire?: () => Promise<DurableLeaseToken | undefined>;
}): Promise<T | undefined> {
	const { ownership, originKey, ownerId, ttlMs, countPending, run, onIdle, onLeaseHeldElsewhere, noteIdleWork = true, acquire } = options;
	const pending = await countPending();
	if (pending === 0) {
		if (noteIdleWork) await ownership.noteWork({ originKey, pendingCount: 0, hasDueWork: false });
		return onIdle?.();
	}
	await ownership.noteWork({ originKey, pendingCount: pending, hasDueWork: true });
	const token = await (acquire ? acquire() : ownership.acquireScheduler({ originKey, ownerId, ttlMs }));
	if (!token) return onLeaseHeldElsewhere?.();
	try {
		return await run();
	} finally {
		const remaining = await countPending();
		await ownership.noteWork({ originKey, pendingCount: remaining, hasDueWork: remaining > 0 });
		await ownership.releaseScheduler({ token, isIdle: async () => (await countPending()) === 0 });
	}
}
